/**
 * Reading a scanned specimen code.
 *
 * The QR on a label and on every sample surface encodes the record's detail
 * URL, not the accession code (`_sample_detail_url` in `backend/server.py`).
 * That is right for a phone camera, which opens the link, but it means the
 * text a scanner produces looks nothing like what a human would type into a
 * search box. A bench scanner in keyboard-wedge mode types that whole URL and
 * presses Enter, and the search matched name, code, client, and project — so
 * scanning found nothing at all.
 *
 * These helpers accept anything a scan can plausibly produce and reduce it to
 * a sample id, so one parser serves the search box, the camera, and a pasted
 * link.
 */

// Ids are `str(uuid.uuid4())` (`new_id` in `backend/tenancy.py`). Matching the
// full canonical shape is what makes a bare id safe to accept: no accession
// code, client name, or project string can be mistaken for one.
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * The sample id inside a scanned string, or null if there isn't one.
 *
 * Accepts a full detail URL (`https://lims.example/samples/<id>`), a bare path
 * (`/samples/<id>`), or the id on its own — which is what the label carries
 * when no origin is configured and `_sample_detail_url` degrades to the id.
 */
export function sampleIdFromScan(text) {
  const trimmed = String(text ?? "").trim();
  if (!trimmed) return null;

  if (UUID.test(trimmed)) return trimmed;

  // Query strings and fragments are stripped rather than rejected: a scanner
  // or a shared link may carry either, and neither changes which record is
  // being asked for.
  const path = trimmed.split(/[?#]/)[0];
  const match = path.match(/\/samples\/([^/]+)\/?$/i);
  if (match && UUID.test(match[1])) return match[1];

  return null;
}

/** Whether a string is a scan rather than something somebody typed. */
export function isScan(text) {
  return sampleIdFromScan(text) !== null;
}
